export interface FaqItem {
  question: string;
  answer: string;
}

// CMS FAQ blob: each Q&A is wrapped in `div.seo-qa`; the question lives in `div.seo-question`
// (V1 used `h3.seo-question`) and the answer is the remaining inner HTML. Question markup is
// flat — nested same-tag elements inside the question element would truncate the match.
const QUESTION = /<(div|h3)\s[^>]*class=["']([^"']*)["'][^>]*>([\s\S]*?)<\/\1>/i;
const TRAILING_CLOSE_DIV = /<\/div>\s*$/i;

const hasClass = (classValue: string, token: string) => classValue.split(/\s+/).includes(token);

const findSeoQaOpenings = (html: string): RegExpExecArray[] => {
  const openings: RegExpExecArray[] = [];
  const divOpen = /<div\s[^>]*class=["']([^"']*)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = divOpen.exec(html)) !== null) {
    const classValue = match[1];
    if (classValue && hasClass(classValue, "seo-qa")) {
      openings.push(match);
    }
  }
  return openings;
};

/**
 * Splits the generic-FAQ CMS blob into per-question items using string operations only
 * (isomorphic / SSR-safe — no DOMParser). Each answer spans to the next wrapper's opening,
 * so prose between Q&A wrappers lands in the preceding answer. Parsing is all-or-nothing:
 * any wrapper that doesn't match the expected structure yields no items, so callers fall
 * back to the raw render instead of silently dropping that Q&A.
 */
export const parseFaqItems = (html: string | null | undefined): FaqItem[] => {
  if (!html) {
    return [];
  }

  const openings = findSeoQaOpenings(html);

  const items: FaqItem[] = [];
  for (let index = 0; index < openings.length; index += 1) {
    const opening = openings[index];
    if (!opening) {
      continue;
    }

    const nextOpening = openings[index + 1];
    const start = opening.index + (opening[0]?.length ?? 0);
    const end = nextOpening ? nextOpening.index : html.length;
    const inner = html.slice(start, end).replace(TRAILING_CLOSE_DIV, "").trim();

    const questionMatch = inner.match(QUESTION);
    if (!questionMatch || questionMatch.index === undefined) {
      return [];
    }

    const questionClass = questionMatch[2];
    const questionHtml = questionMatch[3];
    if (!questionClass || !hasClass(questionClass, "seo-question") || !questionHtml) {
      return [];
    }

    const question = questionHtml.trim();
    if (!question) {
      return [];
    }

    const questionTag = questionMatch[0] ?? "";
    const answer = (inner.slice(0, questionMatch.index) + inner.slice(questionMatch.index + questionTag.length)).trim();

    items.push({ question, answer });
  }

  return items;
};

export interface SeoBodySplit {
  prose: string;
  items: FaqItem[];
  trailing: string;
}

const findSeoQaBlockEnd = (html: string, openEnd: number): number => {
  const divTag = /<\/?div\b[^>]*>/gi;
  divTag.lastIndex = openEnd;
  let depth = 1;
  let match: RegExpExecArray | null;
  while ((match = divTag.exec(html)) !== null) {
    depth += match[0][1] === "/" ? -1 : 1;
    if (depth === 0) {
      return match.index + match[0].length;
    }
  }
  return html.length;
};

/**
 * Splits a CMS SEO body into leading prose, Q&A items, and prose trailing the last Q&A, so the
 * prose renders expanded while the Q&A collapses into accordion rows. Content between Q&A
 * wrappers stays part of the preceding answer. Bodies without seo-qa wrappers — or whose Q&A
 * does not fully parse — return whole as prose.
 */
export const splitSeoBody = (html: string | null | undefined): SeoBodySplit => {
  if (!html) {
    return { prose: "", items: [], trailing: "" };
  }

  const openings = findSeoQaOpenings(html);
  const first = openings[0];
  const last = openings.at(-1);
  if (!first || !last) {
    return { prose: html, items: [], trailing: "" };
  }

  const lastBlockEnd = findSeoQaBlockEnd(html, last.index + (last[0]?.length ?? 0));
  const items = parseFaqItems(html.slice(0, lastBlockEnd));
  if (items.length === 0) {
    return { prose: html, items: [], trailing: "" };
  }

  return {
    prose: html.slice(0, first.index).trim(),
    items,
    trailing: html.slice(lastBlockEnd).trim(),
  };
};
